import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, FileDown, Loader2, Plus, Trash2 } from "lucide-react";

const STATUSES = ["New", "Received", "Teardown", "Parts Ordered", "Rebuilt", "Ready for Pickup", "Picked Up / Paid"];
const statusClass = (s) => `status-${s.replace(/\s+/g, "").replace("/", "")}`;

export default function AdminJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [partForm, setPartForm] = useState({ description: "", qty: 1, unit_price: 0 });
  const [inv, setInv] = useState({ labor_hours: 2, labor_rate: 75, deposit_credit: 0, tax_rate: 0, notes: "" });
  const nav = useNavigate();

  const load = async () => {
    try { const r = await api.get(`/admin/jobs/${id}`); setJob(r.data); }
    catch (e) { if (e.response?.status === 401) nav("/admin/login"); else toast.error("Not found"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (job) setInv(i => ({ ...i, deposit_credit: job.deposit_status === "paid" ? (job.deposit_amount || 150) : 0 }));
  }, [job]);

  const setStatus = async (s) => {
    try { await api.patch(`/admin/jobs/${id}/status`, { status: s }); toast.success(`Status → ${s}. Customer emailed.`); load(); }
    catch { toast.error("Failed"); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/admin/jobs/${id}/notes`, { text: noteText });
    setNoteText(""); load();
  };
  const addPart = async () => {
    if (!partForm.description.trim()) return;
    await api.post(`/admin/jobs/${id}/parts`, partForm);
    setPartForm({ description: "", qty: 1, unit_price: 0 }); load();
  };
  const delPart = async (pid) => { await api.delete(`/admin/jobs/${id}/parts/${pid}`); load(); };

  const downloadInvoice = async () => {
    try {
      const r = await api.post(`/admin/jobs/${id}/invoice`, { ...inv, parts: job.parts || [] }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${id.slice(0,8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Invoice failed"); }
  };

  if (!job) return <div className="min-h-screen bg-[#0D0E11] flex items-center justify-center text-slate-400">Loading…</div>;

  const c = job.customer;
  const partsTotal = (job.parts || []).reduce((s, p) => s + (p.qty * p.unit_price), 0);
  const subtotal = inv.labor_hours * inv.labor_rate + partsTotal;
  const total = subtotal + subtotal * (inv.tax_rate / 100) - inv.deposit_credit;

  return (
    <div className="min-h-screen bg-[#0D0E11] diamond-plate">
      <Toaster theme="dark" />
      <header className="border-b border-[#2A2F3A] bg-[#0D0E11]/95 sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-4 flex items-center gap-4">
          <Link to="/admin" data-testid="job-back" className="btn-outline p-2 rounded-sm"><ArrowLeft className="w-4 h-4" /></Link>
          <div className="flex-1">
            <div className="font-display font-black text-white text-xl uppercase">{c.name}</div>
            <div className="eyebrow">Job #{id.slice(0,8)} · {job.job_type === "onsite" ? "On-Site" : "Rebuild"}</div>
          </div>
          <span className={`pill ${statusClass(job.status)}`}>{job.status}</span>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 sm:px-8 py-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Status pipeline */}
          <div className="shop-card p-5">
            <div className="eyebrow mb-3">Update Status → Customer Gets Emailed</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map(s => (
                <button key={s} data-testid={`set-status-${s.replace(/\s+/g,"-").toLowerCase()}`} onClick={() => setStatus(s)}
                  className={`pill ${statusClass(s)} ${job.status === s ? "ring-2 ring-white/50" : "opacity-70 hover:opacity-100"}`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Customer & equipment */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="shop-card p-5">
              <div className="eyebrow mb-3">Customer</div>
              <Info label="Phone" value={<a href={`tel:${c.phone}`} className="text-red-500 underline">{c.phone}</a>} />
              <Info label="Email" value={<a href={`mailto:${c.email}`} className="text-red-500 underline">{c.email}</a>} />
              <Info label="Company" value={c.company || "—"} />
              <Info label="Preferred" value={c.preferred_contact} />
            </div>
            <div className="shop-card p-5">
              <div className="eyebrow mb-3">Equipment</div>
              {job.job_type === "onsite" ? (
                <>
                  <Info label="Equipment" value={job.onsite?.equipment_description} />
                  <Info label="Problem" value={<span className="text-slate-300">{job.onsite?.problem_description}</span>} />
                  <Info label="Address" value={job.onsite?.address} />
                  <Info label="Distance" value={`${job.onsite?.distance_miles} miles`} />
                  <Info label="Emergency" value={job.onsite?.is_emergency ? <span className="text-amber-400 font-bold">YES</span> : "No"} />
                </>
              ) : (
                <>
                  <Info label="Make/Model" value={`${job.equipment?.make} ${job.equipment?.model}`} />
                  <Info label="Symptoms" value={(job.symptoms || []).join(", ") || "—"} />
                  {job.other_symptom && <Info label="Other" value={job.other_symptom} />}
                </>
              )}
            </div>
          </div>

          {/* Specs (rebuild) */}
          {job.job_type === "rebuild" && (
            <div className="shop-card p-5">
              <div className="eyebrow mb-3">Cylinder Specs</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["bore", "rod_diameter", "stroke", "pin_to_pin", "port_size", "seal_kit_number", "oem_part_number"].map(k => (
                  <div key={k}>
                    <div className="text-slate-500 text-xs font-mono-tech uppercase">{k.replace(/_/g, " ")}</div>
                    <div className="text-white font-display font-bold">{job.specs?.[k] || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photos */}
          {job.photos?.length > 0 && (
            <div className="shop-card p-5">
              <div className="eyebrow mb-3">Photos</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {job.photos.map(p => (
                  <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer" className="block aspect-square bg-black rounded-sm overflow-hidden border border-[#2A2F3A]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="cyl" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="shop-card p-5">
            <div className="eyebrow mb-3">Internal Notes</div>
            <div className="space-y-2">
              {(job.internal_notes || []).map(n => (
                <div key={n.id} className="text-slate-300 text-sm border-l-2 border-red-500 pl-3">
                  <div>{n.text}</div>
                  <div className="text-slate-500 text-xs font-mono-tech mt-1">{new Date(n.at).toLocaleString()}</div>
                </div>
              ))}
              {(!job.internal_notes || job.internal_notes.length === 0) && <div className="text-slate-500 text-sm">No notes yet.</div>}
            </div>
            <div className="mt-3 flex gap-2">
              <input data-testid="note-input" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note…" className="input flex-1" />
              <button data-testid="note-add" onClick={addNote} className="btn-red px-4 py-2 rounded-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
            </div>
          </div>

          {/* Parts */}
          <div className="shop-card p-5">
            <div className="eyebrow mb-3">Parts</div>
            <div className="space-y-2">
              {(job.parts || []).map(p => (
                <div key={p.id} className="flex items-center gap-3 text-slate-200 text-sm bg-[#0D0E11] rounded-sm px-3 py-2">
                  <div className="flex-1">{p.description}</div>
                  <div className="text-slate-400 font-mono-tech text-xs">{p.qty} × ${p.unit_price.toFixed(2)}</div>
                  <div className="text-white font-display font-bold">${(p.qty * p.unit_price).toFixed(2)}</div>
                  <button data-testid={`del-part-${p.id.slice(0,8)}`} onClick={() => delPart(p.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-6 gap-2">
              <input data-testid="part-desc" value={partForm.description} onChange={e => setPartForm({...partForm, description: e.target.value})} placeholder="Description" className="input col-span-3" />
              <input data-testid="part-qty" type="number" step="0.1" value={partForm.qty} onChange={e => setPartForm({...partForm, qty: parseFloat(e.target.value) || 0})} className="input col-span-1" />
              <input data-testid="part-price" type="number" step="0.01" value={partForm.unit_price} onChange={e => setPartForm({...partForm, unit_price: parseFloat(e.target.value) || 0})} className="input col-span-1" />
              <button data-testid="part-add" onClick={addPart} className="btn-red rounded-sm col-span-1 inline-flex items-center justify-center"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Right sidebar - invoice */}
        <aside className="space-y-6">
          <div className="shop-card p-5">
            <div className="eyebrow mb-2">Deposit</div>
            <div className="font-display font-black text-3xl text-white">${(job.deposit_amount || 0).toFixed(2)}</div>
            <div className={`text-sm font-semibold ${job.deposit_status === "paid" ? "text-green-400" : "text-amber-400"}`}>{(job.deposit_status || "n/a").toUpperCase()}</div>
          </div>

          <div className="shop-card p-5">
            <div className="eyebrow mb-3">Generate Invoice</div>
            <F label="Labor Hours"><input data-testid="inv-hours" type="number" step="0.25" value={inv.labor_hours} onChange={e => setInv({...inv, labor_hours: parseFloat(e.target.value) || 0})} className="input" /></F>
            <F label="Labor Rate"><input data-testid="inv-rate" type="number" step="1" value={inv.labor_rate} onChange={e => setInv({...inv, labor_rate: parseFloat(e.target.value) || 0})} className="input" /></F>
            <F label="Deposit Credit"><input data-testid="inv-deposit" type="number" step="0.01" value={inv.deposit_credit} onChange={e => setInv({...inv, deposit_credit: parseFloat(e.target.value) || 0})} className="input" /></F>
            <F label="Tax %"><input data-testid="inv-tax" type="number" step="0.01" value={inv.tax_rate} onChange={e => setInv({...inv, tax_rate: parseFloat(e.target.value) || 0})} className="input" /></F>
            <F label="Notes"><textarea data-testid="inv-notes" value={inv.notes} onChange={e => setInv({...inv, notes: e.target.value})} className="input min-h-[70px]" /></F>

            <div className="mt-3 pt-3 border-t border-[#2A2F3A] text-sm">
              <Row label="Labor" value={`$${(inv.labor_hours * inv.labor_rate).toFixed(2)}`} />
              <Row label="Parts" value={`$${partsTotal.toFixed(2)}`} />
              <Row label="Deposit Credit" value={`-$${inv.deposit_credit.toFixed(2)}`} />
              <Row label="Total" value={`$${total.toFixed(2)}`} bold />
            </div>
            <button onClick={downloadInvoice} data-testid="download-invoice" className="btn-red w-full mt-4 py-3 rounded-sm inline-flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download Invoice PDF</button>
          </div>
        </aside>
      </main>
    </div>
  );
}

function Info({ label, value }) { return <div className="mb-2"><div className="text-slate-500 text-xs font-mono-tech uppercase">{label}</div><div className="text-white">{value}</div></div>; }
function F({ label, children }) { return <label className="block mb-3"><div className="eyebrow mb-1">{label}</div>{children}</label>; }
function Row({ label, value, bold }) { return <div className={`flex justify-between py-1 ${bold ? "font-display font-black text-white text-lg pt-2 border-t border-[#2A2F3A] mt-1" : "text-slate-300"}`}><span>{label}</span><span>{value}</span></div>; }
